import { useEffect, useRef, useState } from 'react';

import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { type PartListItem, usePartsList } from '@/lib/parts';

interface PartPickerProps {
  orgSlug: string | undefined;
  selected: PartListItem | null;
  onSelect: (part: PartListItem | null) => void;
  placeholder?: string;
}

export function PartPicker({
  orgSlug,
  selected,
  onSelect,
  placeholder = 'Search part #, mfg PN, or description…',
}: PartPickerProps) {
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const search = usePartsList(orgSlug, {
    q: debouncedQ || undefined,
    limit: 20,
  });

  return (
    <div ref={wrapperRef} className="relative">
      {selected ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 px-3 py-1.5 text-sm">
          <div className="min-w-0 truncate">
            <span className="font-mono text-stone-900 dark:text-stone-100">{selected.part_number}</span>
            {selected.mfg_pn && (
              <span className="ml-2 font-mono text-xs text-stone-500 dark:text-stone-400">{selected.mfg_pn}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              setQuery('');
            }}
            className="text-xs text-stone-400 dark:text-stone-500 hover:text-stone-700 dark:hover:text-stone-200"
          >
            change
          </button>
        </div>
      ) : (
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
        />
      )}

      {open && !selected && debouncedQ && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-y-auto rounded-md border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-lg">
          {search.isPending && (
            <p className="px-3 py-2 text-sm text-stone-400 dark:text-stone-500">Searching…</p>
          )}
          {search.data && search.data.items.length === 0 && (
            <p className="px-3 py-2 text-sm text-stone-500 dark:text-stone-400">No matches.</p>
          )}
          {search.data?.items.map((part) => (
            <button
              key={part.id}
              type="button"
              onClick={() => {
                onSelect(part);
                setOpen(false);
                setQuery('');
              }}
              className={cn(
                'block w-full cursor-pointer border-b border-stone-100 dark:border-stone-800 px-3 py-2 text-left text-sm last:border-0',
                'hover:bg-forge-50 dark:hover:bg-stone-800',
              )}
            >
              <div className="flex items-center gap-2">
                <span className="font-mono text-stone-900 dark:text-stone-100">{part.part_number}</span>
                {part.mfg_pn && (
                  <span className="font-mono text-xs text-stone-500 dark:text-stone-400">{part.mfg_pn}</span>
                )}
              </div>
              {part.description && (
                <div className="mt-0.5 truncate text-xs text-stone-500 dark:text-stone-400">
                  {part.description}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
